import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, formatCurrencyFromCents, formatDate, formatDateTime } from '../../utils/api'
import { printrunRowState } from '../../utils/printrunStatus'
import { MetricsRow } from '../shared'

const MARKETPLACES = [
    { value: 'all', label: 'All' },
    { value: 'shopify', label: 'Shopify' },
    { value: 'reverb', label: 'Reverb' },
    { value: 'ebay', label: 'eBay' },
]

function PrintrunsTab() {
    const [searchParams] = useSearchParams()
    const highlightId = searchParams.get('highlight')

    const [marketplace, setMarketplace] = useState('all')
    const [unprinted, setUnprinted] = useState([])
    const [unprintedLoading, setUnprintedLoading] = useState(false)
    const [unprintedError, setUnprintedError] = useState(null)
    const [printruns, setPrintruns] = useState([])
    const [printrunsLoading, setPrintrunsLoading] = useState(false)
    const [printrunsError, setPrintrunsError] = useState(null)
    const [creating, setCreating] = useState(false)
    const [toast, setToast] = useState(null)
    const [toastTone, setToastTone] = useState('success') // 'success' | 'error'
    const [refreshKey, setRefreshKey] = useState(0)

    const pollersRef = useRef(new Map())

    // Unprinted list — re-runs on marketplace change or explicit refresh.
    useEffect(() => {
        const controller = new AbortController()
        let active = true
        setUnprintedLoading(true)

        setUnprintedError(null)
        api.get(`/api/printruns/unprinted?marketplace=${marketplace}`, { signal: controller.signal })
            .then(data => { if (active) setUnprinted(data.items || []) })
            .catch(err => {
                if (err.name === 'AbortError') return
                console.error('Failed to load unprinted orders:', err)
                if (active) {
                    setUnprinted([])
                    setUnprintedError(`${err.status ? `HTTP ${err.status}: ` : ''}${err.message || 'Unknown error'}`)
                }
            })
            .finally(() => { if (active) setUnprintedLoading(false) })

        return () => { active = false; controller.abort() }
    }, [marketplace, refreshKey])

    // Printrun history.
    useEffect(() => {
        const controller = new AbortController()
        let active = true
        setPrintrunsLoading(true)

        setPrintrunsError(null)
        api.get('/api/printruns?limit=50', { signal: controller.signal })
            .then(data => { if (active) setPrintruns(data.items || []) })
            .catch(err => {
                if (err.name === 'AbortError') return
                console.error('Failed to load printruns:', err)
                if (active) {
                    setPrintruns([])
                    setPrintrunsError(`${err.status ? `HTTP ${err.status}: ` : ''}${err.message || 'Unknown error'}`)
                }
            })
            .finally(() => { if (active) setPrintrunsLoading(false) })

        return () => { active = false; controller.abort() }
    }, [refreshKey])

    // Per-update poller diff: only stop/start when a row changes state.
    useEffect(() => {
        const pollers = pollersRef.current
        const renderingIds = new Set(
            printruns.filter(p => p.pdf_status === 'rendering').map(p => p.id)
        )

        for (const [id, handle] of pollers.entries()) {
            if (!renderingIds.has(id)) {
                clearInterval(handle)
                pollers.delete(id)
            }
        }

        for (const id of renderingIds) {
            if (pollers.has(id)) continue
            const handle = setInterval(async () => {
                try {
                    const fresh = await api.get(`/api/printruns/${id}`)
                    if (fresh.pdf_status !== 'rendering') {
                        setPrintruns(curr => curr.map(p => p.id === id ? { ...p, ...fresh } : p))
                    }
                } catch (err) {
                    console.error('Poll error for', id, err)
                }
            }, 1500)
            pollers.set(id, handle)
        }
    }, [printruns])

    // Unmount cleanup.
    useEffect(() => () => {
        for (const handle of pollersRef.current.values()) clearInterval(handle)
        pollersRef.current.clear()
    }, [])

    function showToast(message, tone = 'success') {
        setToast(message)
        setToastTone(tone)
        setTimeout(() => setToast(null), 4000)
    }

    async function handleCreate() {
        if (creating || unprinted.length === 0) return
        setCreating(true)
        try {
            const created = await api.post('/api/printruns', { marketplace })
            showToast(`Printrun created (${created.order_count} orders). Generating PDF…`, 'success')
            setRefreshKey(k => k + 1)
        } catch (err) {
            if (err.status === 409) showToast('No unprinted orders to print.', 'error')
            else showToast(`Failed to create printrun: ${err.message}`, 'error')
        } finally {
            setCreating(false)
        }
    }

    async function handleRetry(id) {
        try {
            await api.post(`/api/printruns/${id}/regenerate-pdf`)
            setPrintruns(curr => curr.map(p => p.id === id ? { ...p, pdf_status: 'rendering' } : p))
        } catch (err) {
            showToast(`Retry failed: ${err.message}`, 'error')
        }
    }

    function downloadUrl(id) {
        return `/api/printruns/${id}/pdf`
    }

    const metrics = [
        { label: 'Unprinted orders', value: unprinted.length, color: unprinted.length > 0 ? 'warning' : '' },
        { label: 'Past printruns', value: printruns.length },
    ]

    return (
        <>
            <div style={{ marginTop: 28 }}>
                <MetricsRow metrics={metrics} />
            </div>

            {toast && (
                <div
                    className="card"
                    style={{
                        marginBottom: 'var(--space-4)',
                        padding: 'var(--space-3) var(--space-4)',
                        background: toastTone === 'error' ? 'var(--color-error-light)' : 'var(--color-success-light)',
                        borderLeft: `3px solid ${toastTone === 'error' ? 'var(--color-error)' : 'var(--color-success)'}`,
                    }}
                >
                    {toast}
                </div>
            )}

            {/* Filter + action bar */}
            <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div className="filter-group" style={{ width: 200 }}>
                        <label className="filter-label" htmlFor="printruns-marketplace">Marketplace</label>
                        <select
                            id="printruns-marketplace"
                            className="form-input"
                            value={marketplace}
                            onChange={e => setMarketplace(e.target.value)}
                        >
                            {MARKETPLACES.map(m => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ marginLeft: 'auto' }}>
                        <button
                            className="btn btn-primary"
                            disabled={creating || unprinted.length === 0}
                            onClick={handleCreate}
                        >
                            {creating ? 'Creating…' : 'Create Printrun'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Unprinted orders card */}
            <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="card-header">
                    <h2 className="card-title">Unprinted Orders</h2>
                    <span className="badge badge-info">{unprinted.length}</span>
                </div>
                {unprintedLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
                        <div className="loading-spinner" />
                    </div>
                ) : unprintedError ? (
                    <div className="empty-state" style={{ color: 'var(--color-error)' }}>
                        <div className="empty-state-title">Failed to load unprinted orders</div>
                        <div className="empty-state-description">{unprintedError}</div>
                    </div>
                ) : unprinted.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-title">No unprinted orders</div>
                        <div className="empty-state-description">
                            Nothing to print right now for this marketplace filter.
                        </div>
                    </div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Marketplace</th>
                                <th>Order #</th>
                                <th className="col-mobile-hide">Items</th>
                                <th className="col-mobile-hide">Location</th>
                                <th style={{ textAlign: 'right' }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {unprinted.map(o => (
                                <tr key={`${o.marketplace}-${o.order_number}`}>
                                    <td className="text-sm text-muted">{formatDate(o.order_date)}</td>
                                    <td className="text-sm">{o.marketplace}</td>
                                    <td style={{ fontWeight: 500 }}>{o.order_number}</td>
                                    <td className="col-mobile-hide">{o.item_count}</td>
                                    <td className="col-mobile-hide text-sm" style={{ fontFamily: 'var(--font-mono, Menlo, monospace)' }}>
                                        {o.primary_location || <span className="text-muted">—</span>}
                                    </td>
                                    <td style={{ textAlign: 'right', fontWeight: 500 }}>
                                        {o.total_cents != null
                                            ? formatCurrencyFromCents(o.total_cents, o.currency)
                                            : <span className="text-muted">—</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Past printruns card */}
            <div className="card">
                <div className="card-header">
                    <h2 className="card-title">Past Printruns</h2>
                    <span className="badge badge-neutral">{printruns.length}</span>
                </div>
                {printrunsLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
                        <div className="loading-spinner" />
                    </div>
                ) : printrunsError ? (
                    <div className="empty-state" style={{ color: 'var(--color-error)' }}>
                        <div className="empty-state-title">Failed to load printruns</div>
                        <div className="empty-state-description">{printrunsError}</div>
                    </div>
                ) : printruns.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-title">No printruns yet</div>
                        <div className="empty-state-description">
                            Click <strong>Create Printrun</strong> above to generate your first one.
                        </div>
                    </div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Created</th>
                                <th className="col-mobile-hide">Created by</th>
                                <th>Marketplace</th>
                                <th style={{ textAlign: 'right' }}>Orders</th>
                                <th style={{ textAlign: 'right' }}>PDF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {printruns.map(p => {
                                const state = printrunRowState(p)
                                const isHighlighted = p.id === highlightId
                                return (
                                    <tr
                                        key={p.id}
                                        style={isHighlighted
                                            ? { background: 'var(--color-warning-light, #FFF6D6)' }
                                            : undefined}
                                    >
                                        <td className="text-sm">{formatDateTime(p.created_at)}</td>
                                        <td className="text-sm col-mobile-hide">{p.created_by?.name || '—'}</td>
                                        <td className="text-sm">{p.marketplace}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 500 }}>{p.order_count}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            {state === 'rendering' && (
                                                <span className="text-sm text-muted">
                                                    <span className="loading-spinner loading-spinner-sm" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }} />
                                                    Rendering…
                                                </span>
                                            )}
                                            {state === 'ready' && (
                                                <a className="btn btn-sm btn-secondary" href={downloadUrl(p.id)}>
                                                    Download
                                                </a>
                                            )}
                                            {state === 'failed' && (
                                                <button className="btn btn-sm btn-secondary" onClick={() => handleRetry(p.id)}>
                                                    Retry
                                                </button>
                                            )}
                                            {state === 'expired' && (
                                                <span className="text-sm text-muted">PDF expired</span>
                                            )}
                                            {state === 'unknown' && <span className="text-sm text-muted">—</span>}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    )
}

export default PrintrunsTab
